import React, { useEffect, useMemo, useState, Suspense, lazy } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Package, ExternalLink, Minus, Plus, Check, ArrowUpRight, TrendingDown, Flame, Clock, FileCheck2 } from "lucide-react";
import { fetchProduct } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { DoseCalculator } from "@/components/DoseCalculator";
import { RecentlyViewedRail, pushRecentlyViewed } from "@/components/RecentlyViewedRail";
// Code-split the WebGL 3D viewer (~600KB of 3Dmol.js) off the critical path so the product
// page renders FAST and the molecule downloads only after first paint.
const MoleculeViewer = lazy(() => import("@/components/MoleculeViewer").then((m) => ({ default: m.MoleculeViewer })));
const DualMoleculeViewer = lazy(() => import("@/components/MoleculeViewer").then((m) => ({ default: m.DualMoleculeViewer })));
import { toast } from "sonner";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SoldOutNotify({ productId }) {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!email.includes("@")) { toast.error("Valid email required"); return; }
        try {
            await axios.post(`${API_BASE}/restock-notify`, { product_id: productId, email });
            setSent(true);
            toast.success("We'll email you when it's back in stock");
        } catch {
            toast.error("Could not submit. Try again");
        }
    };
    if (sent) {
        return (
            <div className="mt-4 p-4 border border-neon/40 bg-neon/5" data-testid="restock-confirmation">
                <p className="overline text-neon text-[10px]">✓ NOTIFICATION SET</p>
                <p className="text-sm text-ink mt-1">We'll email <strong>{email}</strong> the moment it's back.</p>
            </div>
        );
    }
    return (
        <form onSubmit={submit} className="mt-4 p-4 border border-red-500/40 bg-red-950/20" data-testid="sold-out-notify">
            <p className="overline text-red-400 text-[10px] mb-2">EMAIL ME WHEN BACK IN STOCK</p>
            <div className="flex gap-2">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@lab.com"
                    required
                    className="flex-1 border border-ink-strong bg-ink-base text-ink px-3 py-2.5 text-sm focus:outline-none focus:border-neon"
                    data-testid="restock-email-input"
                />
                <button type="submit" className="bg-neon hover:bg-neon-glow text-black px-5 py-2.5 text-xs font-bold uppercase tracking-wide" data-testid="restock-submit">
                    NOTIFY ME
                </button>
            </div>
            <p className="mt-2 text-[11px] text-ink-mute">One email when restocked. We don't share your address.</p>
        </form>
    );
}

const DEFAULT_BUNDLE_TIERS = [
    { qty: 3, pct: 0.10, label: "3-VIAL MIX", desc: "10% OFF · Mix & match any dosage" },
    { qty: 5, pct: 0.15, label: "5-VIAL STACK", desc: "15% OFF · Most researchers choose this" },
    { qty: 10, pct: 0.20, label: "10-PACK BULK", desc: "20% OFF · Max-research economy" },
];

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { formatPrice, addToCart, currency, cadRate } = useApp();
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [initialLoad, setInitialLoad] = useState(true);
    const [coaOpen, setCoaOpen] = useState(false);
    const [labPhotoOpen, setLabPhotoOpen] = useState(null);  // {url, caption, label} or null

    useEffect(() => {
        let cancelled = false;
        // Scroll to top on variant / product change
        if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "auto" });
        fetchProduct(slug)
            .then((p) => {
                if (!cancelled) {
                    setProduct(p);
                    setInitialLoad(false);
                    setQty(1);
                    // Track in Recently Viewed (localStorage, no API call)
                    if (p?.id) pushRecentlyViewed(p.id);
                }
            })
            .catch(() => { if (!cancelled) setInitialLoad(false); });
        return () => { cancelled = true; };
    }, [slug]);

    const variants = product?.variants || null;
    const hasVariants = variants && variants.length > 1;
    const soldOut = product ? product.in_stock === false : false;
    const isPreorder = !!product?.preorder;
    const isBlend = product?.molecule?.dual;

    // Build bundle-tier display, prefer flat_bundles from backend (per-SKU flat prices)
    const bundleCalc = useMemo(() => {
        if (!product) return null;
        if (product.flat_bundles && product.flat_bundles.length) {
            return product.flat_bundles.map((fb) => {
                const unit = product.price_usd;
                const full = unit * fb.min_qty;
                const discounted = fb.flat_total_usd;
                const savings = Math.max(0, full - discounted);
                const pct = full > 0 ? savings / full : 0;
                const perVial = discounted / fb.min_qty;
                const tierLabel = fb.min_qty === 3 ? "3-VIAL MIX" : fb.min_qty === 5 ? "5-VIAL STACK" : fb.min_qty === 10 ? "10-PACK BULK" : `${fb.min_qty}-PACK`;
                const desc = fb.min_qty === 3 ? `Save ${Math.round(pct * 100)}% · Mix & match any dosage`
                    : fb.min_qty === 5 ? `Save ${Math.round(pct * 100)}% · Most researchers choose this`
                    : `Save ${Math.round(pct * 100)}% · Max-research economy`;
                return { qty: fb.min_qty, pct, label: tierLabel, desc, full, discounted, savings, perVial, isFlat: true };
            });
        }
        // Fallback to default percentage tiers
        return DEFAULT_BUNDLE_TIERS.map((t) => {
            const unit = product.price_usd;
            const full = unit * t.qty;
            const discounted = full * (1 - t.pct);
            const savings = full - discounted;
            const perVial = discounted / t.qty;
            return { ...t, full, discounted, savings, perVial, isFlat: false };
        });
    }, [product]);

    const activeTier = useMemo(() => {
        if (!bundleCalc) return null;
        return [...bundleCalc].reverse().find((t) => qty >= t.qty) || null;
    }, [bundleCalc, qty]);

    if (initialLoad && !product) return <div className="bg-ink-base min-h-screen"><div className="max-w-7xl mx-auto px-6 py-24 text-ink-dim">Loading…</div></div>;
    if (!product) return <div className="bg-ink-base min-h-screen"><div className="max-w-7xl mx-auto px-6 py-24 text-ink">Product not found. <Link to="/shop" className="text-neon underline">Back to shop</Link></div></div>;

    const handleAdd = () => {
        addToCart(product, qty);
        toast.success(`${product.name} ${product.dosage} added to cart`, { description: `${qty} × ${product.dosage}${activeTier ? ` · ${activeTier.label} applied` : ""}` });
    };

    const currentUnit = product.price_usd;
    const currentTotal = currentUnit * qty;
    let currentFinal;
    if (activeTier && activeTier.isFlat) {
        const groups = Math.floor(qty / activeTier.qty);
        const remainder = qty - (groups * activeTier.qty);
        currentFinal = (activeTier.discounted * groups) + (remainder * currentUnit);
    } else if (activeTier) {
        currentFinal = currentTotal * (1 - activeTier.pct);
    } else {
        currentFinal = currentTotal;
    }
    const currentDiscount = currentTotal - currentFinal;

    // Dual-currency display helpers
    const dualPrice = (usd) => {
        if (currency === "cad") {
            const cad = usd * cadRate;
            return { primary: `CA$${cad.toFixed(0)}`, secondary: `≈ $${usd.toFixed(0)} USD` };
        }
        const cad = usd * cadRate;
        return { primary: `$${usd.toFixed(0)} USD`, secondary: `≈ CA$${cad.toFixed(0)}` };
    };

    return (
        <div className="bg-ink-base text-ink min-h-screen" data-testid="product-page">
            <ProductSEO product={product} />
            <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-12">
                <div className="text-xs font-mono uppercase tracking-wider text-ink-mute mb-8">
                    <Link to="/" className="hover:text-ink">Index</Link> / <Link to="/shop" className="hover:text-ink">Catalog</Link> / <Link to={`/shop?cat=${encodeURIComponent(product.category)}`} className="hover:text-ink">{product.category}</Link> / <span className="text-neon">{product.name} {product.variant_label || product.dosage}</span>
                </div>

                <div className="grid md:grid-cols-[1fr_1fr] gap-12 lg:gap-16">
                    {/* 3D MOLECULAR VIEWER (primary visual) */}
                    <div className="md:sticky md:top-24 md:self-start">
                        <div className="aspect-square border border-ink bg-ink-surface overflow-hidden relative" data-testid="product-main-image">
                            <Suspense fallback={
                                <div className="absolute inset-0 flex items-center justify-center" data-testid="molecule-suspense">
                                    <div className="flex flex-col items-center gap-3 opacity-50">
                                        <div className="relative w-12 h-12">
                                            <div className="absolute inset-0 border border-neon/30 rounded-full animate-pulse" />
                                            <div className="absolute inset-2 border border-neon/40 rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
                                            <div className="absolute inset-4 bg-neon/40 rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} />
                                        </div>
                                        <p className="overline text-[9px] text-ink-mute tracking-[0.2em] font-mono">QUEUED</p>
                                    </div>
                                </div>
                            }>
                                {isBlend ? (
                                    <DualMoleculeViewer left={product.molecule.left} right={product.molecule.right} className="w-full h-full" showCaption={false} variant="detail" />
                                ) : (
                                    <MoleculeViewer
                                        molecule={product.molecule || {}}
                                        variant="detail"
                                        showControls={true}
                                        className="w-full h-full"
                                    />
                                )}
                            </Suspense>
                            <div className="absolute top-4 left-4 overline text-neon bg-ink-base/80 backdrop-blur px-2 py-1 border border-neon/30 z-20">LOT #HXR-{product.id.slice(0,4).toUpperCase()}</div>
                            {isPreorder && (
                                <div className="absolute top-4 right-4 overline text-black bg-amber-500 px-3 py-1.5 font-bold z-20" data-testid="preorder-badge-detail">
                                    ◆ PRE-ORDER · 10 to 18 DAYS
                                </div>
                            )}
                            {!isPreorder && product.best_value && (
                                <div className="absolute top-4 right-4 overline text-black bg-gold px-3 py-1.5 font-bold z-20">★ BEST VALUE</div>
                            )}
                        </div>

                        {/* Vials gallery, shows the lab + "what you'll receive" photos.
                            Per-product photography (when uploaded by admin) appears first, falling back to
                            the lab_photos list. URLs are deduped so the same image never tiles twice. */}
                        <div className="mt-3 grid grid-cols-4 gap-2" data-testid="vials-gallery">
                            {(() => {
                                const seen = new Set();
                                const tiles = [];
                                // Front product photo (admin-uploaded), most prominent gallery slot when present
                                if (product.front_image && !seen.has(product.front_image)) {
                                    seen.add(product.front_image);
                                    tiles.push({
                                        url: product.front_image,
                                        label: `${product.name.toUpperCase()} · PRODUCT PHOTO`,
                                        caption: `${product.name} ${product.variant_label || product.dosage}. Actual product photograph. Sterile lyophilized peptide, lot-coded labeling, vacuum-sealed crimped rubber stopper.`,
                                    });
                                }
                                // Optional per-product images (rare, admin can upload via product overrides)
                                const productImgs = (product.images && product.images.length ? product.images : []);
                                for (const url of productImgs) {
                                    if (!url || seen.has(url)) continue;
                                    seen.add(url);
                                    tiles.push({
                                        url,
                                        label: `${product.name.toUpperCase()} · BATCH PHOTO`,
                                        caption: `${product.name} ${product.variant_label || product.dosage}. Sterile lyophilized peptide, vacuum-sealed crimped rubber stopper, lot-coded label.`,
                                    });
                                }
                                // Lab photos (vial-box + Hangzhou lab), admin-editable in Settings → Lab Photos
                                for (const lp of (product.lab_photos || [])) {
                                    if (!lp?.url || seen.has(lp.url)) continue;
                                    seen.add(lp.url);
                                    tiles.push({ url: lp.url, label: lp.label, caption: lp.caption });
                                }
                                const finalTiles = tiles.slice(0, 4);
                                while (finalTiles.length < 4) finalTiles.push(null);
                                return finalTiles.map((tile, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => tile && setLabPhotoOpen(tile)}
                                        className={`aspect-square bg-ink-surface border border-ink-strong overflow-hidden flex items-center justify-center transition-all ${tile ? "hover:border-neon hover:shadow-[0_0_0_1px_rgba(16,185,129,0.4)] cursor-zoom-in" : "cursor-default"}`}
                                        data-testid={`gallery-tile-${i}`}
                                    >
                                        {tile ? (
                                            <img src={tile.url} alt={tile.label} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="text-center px-2">
                                                <div className="font-mono text-[9px] text-ink-mute tracking-wider">SLOT · {String(i + 1).padStart(2, "0")}</div>
                                                <div className="text-[9px] text-ink-mute mt-1">PHOTO COMING</div>
                                            </div>
                                        )}
                                    </button>
                                ));
                            })()}
                        </div>

                        {/* Prominent View Certificate button */}
                        <button
                            type="button"
                            onClick={() => setCoaOpen(true)}
                            className="mt-3 w-full flex items-center justify-between gap-3 bg-neon hover:bg-neon-glow text-black px-5 py-4 transition-colors font-bold"
                            data-testid="view-coa-btn"
                        >
                            <span className="flex items-center gap-3">
                                <FileCheck2 className="w-5 h-5" strokeWidth={2.2} />
                                <span className="text-left">
                                    <span className="block text-[13px] uppercase tracking-wider">View Certificate of Analysis</span>
                                    <span className="block text-[10px] font-mono font-normal opacity-80">Janoshik Labs · third-party verified</span>
                                </span>
                            </span>
                            <ExternalLink className="w-4 h-4" />
                        </button>

                        {/* COA lightbox, portal rendered to body to escape sticky parent stacking context */}
                        {coaOpen && ReactDOM.createPortal(
                            <div
                                className="fixed inset-0 bg-black flex flex-col"
                                style={{ zIndex: 10000 }}
                                data-testid="coa-lightbox"
                            >
                                {/* Slim top bar with close */}
                                <div className="flex items-center justify-between px-5 py-3 border-b border-ink-strong bg-ink-base shrink-0">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <FileCheck2 className="w-5 h-5 text-neon shrink-0" strokeWidth={2} />
                                        <div className="min-w-0">
                                            <p className="overline text-neon text-[10px] tracking-[0.2em]">CERTIFICATE OF ANALYSIS · JANOSHIK LABS</p>
                                            <p className="text-sm text-ink font-mono truncate">{product.name} · {product.variant_label || product.dosage}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setCoaOpen(false)}
                                        className="w-10 h-10 flex items-center justify-center text-ink-dim hover:text-ink hover:bg-ink-surface transition-colors border border-ink-strong"
                                        data-testid="coa-close"
                                        aria-label="Close"
                                    >
                                        <span className="block text-2xl leading-none">×</span>
                                    </button>
                                </div>

                                {/* Full-height image area */}
                                <div
                                    className="flex-1 overflow-auto bg-black flex items-center justify-center p-4"
                                    onClick={() => setCoaOpen(false)}
                                >
                                    {product.coa_image_url ? (
                                        <img
                                            src={product.coa_image_url}
                                            alt={`${product.name} Janoshik COA`}
                                            onClick={(e) => e.stopPropagation()}
                                            className="max-w-full h-auto shadow-2xl cursor-default"
                                            style={{ maxHeight: "calc(100vh - 120px)" }}
                                        />
                                    ) : (
                                        <div className="text-center px-6">
                                            <FileCheck2 className="w-16 h-16 text-ink-mute mx-auto" strokeWidth={1} />
                                            <p className="mt-4 text-ink">COA image not yet published for this lot.</p>
                                            <p className="mt-1 text-xs text-ink-dim">Contact orders@helixorapeptides.com to request the full report.</p>
                                        </div>
                                    )}
                                </div>
                            </div>,
                            document.body
                        )}

                        {/* Lab / Vial photo lightbox, opens when any gallery tile is clicked or when "What you'll receive" CTA fires */}
                        {labPhotoOpen && ReactDOM.createPortal(
                            <div
                                className="fixed inset-0 bg-black/95 flex flex-col"
                                style={{ zIndex: 10000 }}
                                onClick={() => setLabPhotoOpen(null)}
                                data-testid="lab-photo-lightbox"
                            >
                                <div className="flex items-center justify-between px-5 py-3 border-b border-ink-strong bg-ink-base shrink-0">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <Package className="w-5 h-5 text-neon shrink-0" strokeWidth={2} />
                                        <p className="overline text-neon text-[10px] tracking-[0.2em] truncate">{labPhotoOpen.label}</p>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setLabPhotoOpen(null); }}
                                        className="w-10 h-10 flex items-center justify-center text-ink-dim hover:text-ink hover:bg-ink-surface transition-colors border border-ink-strong"
                                        data-testid="lab-photo-close"
                                        aria-label="Close"
                                    >
                                        <span className="block text-2xl leading-none">×</span>
                                    </button>
                                </div>
                                <div className="flex-1 overflow-auto flex items-center justify-center p-4 sm:p-8">
                                    <img
                                        src={labPhotoOpen.url}
                                        alt={labPhotoOpen.label}
                                        onClick={(e) => e.stopPropagation()}
                                        className="max-w-full h-auto shadow-2xl cursor-default"
                                        style={{ maxHeight: "calc(100vh - 200px)" }}
                                    />
                                </div>
                                <div className="px-5 py-4 bg-ink-base border-t border-ink-strong shrink-0" onClick={(e) => e.stopPropagation()}>
                                    <p className="text-sm text-ink-dim leading-relaxed max-w-3xl">{labPhotoOpen.caption}</p>
                                </div>
                            </div>,
                            document.body
                        )}

                        {/* What you'll receive, surfaces the unmarked-packaging photo + Hangzhou lab shot. */}
                        {(product.lab_photos || []).length > 0 && (
                            <div className="mt-5 p-5 border border-ink bg-ink-surface" data-testid="what-youll-receive">
                                <p className="overline text-neon mb-2 text-[10px]">/ WHAT YOU WILL RECEIVE</p>
                                <p className="text-sm text-ink-dim leading-relaxed mb-4">
                                    Real photographs of your peptides, boxed, lot-labeled, and shipped in plain unmarked packaging from our partner research lab.
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    {product.lab_photos.map((lp, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => setLabPhotoOpen(lp)}
                                            className="group relative aspect-[4/3] overflow-hidden border border-ink-strong hover:border-neon transition-all cursor-zoom-in"
                                            data-testid={`lab-photo-${i}`}
                                        >
                                            <img src={lp.url} alt={lp.label} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                                            <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
                                                <p className="overline text-neon text-[9px] mb-1 tracking-[0.18em]">{lp.label}</p>
                                                <p className="text-[10px] text-ink-dim line-clamp-2 leading-snug">{lp.caption.split(".")[0]}.</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <p className="mt-3 text-[10px] font-mono text-ink-mute">More batch photos and lab footage coming soon.</p>
                            </div>
                        )}

                        {/* Amino acid sequence / structure note */}
                        {product.sequence_info && (
                            <div className="mt-3 p-4 border border-ink bg-ink-surface" data-testid="sequence-block">
                                <p className="overline text-[10px] text-gold mb-2">PEPTIDE SEQUENCE · STRUCTURE NOTE</p>
                                <p className="font-mono text-[11px] text-neon break-all leading-relaxed">{product.sequence_info.sequence}</p>
                                <p className="text-xs text-ink-dim mt-2 leading-relaxed">{product.sequence_info.note}</p>
                            </div>
                        )}

                        {/* Pharmacokinetic half-life snapshot */}
                        {product.half_life_info && (
                            <div className="mt-3 p-5 border border-neon/30 bg-neon/5" data-testid="half-life-block">
                                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                    <p className="overline text-[10px] text-neon">⏱ HALF-LIFE · PHARMACOKINETIC PROFILE</p>
                                    <p className="font-mono text-[10px] text-ink-mute">{product.half_life_info.label}</p>
                                </div>
                                <p className="font-display text-3xl sm:text-4xl font-bold text-neon mt-3 leading-tight tracking-tight">{product.half_life_info.value}</p>
                                {product.half_life_info.note && (
                                    <p className="text-xs text-ink-dim mt-3 leading-relaxed">{product.half_life_info.note}</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* DETAILS */}
                    <div className="md:sticky md:top-32 md:self-start">
                        <p className="overline text-neon mb-4">{product.category} · RESEARCH GRADE</p>
                        <h1 className="font-display text-5xl sm:text-6xl font-bold text-ink tracking-tight leading-[0.95]">{product.name}</h1>
                        <p className="mt-3 font-mono text-sm text-ink-dim">{product.variant_label || product.dosage} · Lyophilized Vial{product.variant_group === "retatrutide" ? " · Triple Agonist" : ""}</p>

                        {/* Pre-order notice */}
                        {isPreorder && (
                            <div className="mt-5 flex items-start gap-3 p-4 bg-amber-500/10 border-l-4 border-amber-500" data-testid="preorder-note">
                                <Clock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" strokeWidth={2} />
                                <div>
                                    <p className="font-bold text-amber-500 text-sm uppercase tracking-wide">{product.preorder.label || "Pre-Order"}</p>
                                    <p className="text-xs text-ink-dim mt-1">{product.preorder.note || "Ships in 10 to 18 days · fresh stock from lab"}</p>
                                </div>
                            </div>
                        )}

                        {/* Variant selector */}
                        {variants && variants.length > 1 && (
                            <div className="mt-8" data-testid="variant-selector">
                                <p className="overline text-ink-mute mb-3">SELECT {product.variant_group === "hgh" ? "KIT SIZE" : "DOSAGE"}</p>
                                <div className={`grid ${variants.length > 4 ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-5" : "grid-cols-3"} gap-2`}>
                                    {variants.map((v) => {
                                        const active = v.slug === product.slug;
                                        const vSoldOut = v.in_stock === false;
                                        return (
                                            <button
                                                key={v.id}
                                                onClick={() => navigate(`/product/${v.slug}`)}
                                                className={`relative p-3 border text-left transition-all ${active ? "border-neon bg-ink-surface" : "border-ink-strong bg-ink-base hover:border-ink-strong hover:bg-ink-surface"} ${vSoldOut ? "opacity-60" : ""}`}
                                                data-testid={`variant-${v.variant_label}`}
                                            >
                                                {v.best_value && !vSoldOut && (
                                                    <div className="absolute -top-2 right-2 overline text-[8px] text-black bg-gold px-1.5 py-0.5 font-bold">★ BEST</div>
                                                )}
                                                {vSoldOut && (
                                                    <div className="absolute -top-2 right-2 overline text-[8px] text-white bg-red-600 px-1.5 py-0.5 font-bold">SOLD OUT</div>
                                                )}
                                                <div className="font-display text-base sm:text-lg font-bold text-ink leading-tight">{v.variant_label}</div>
                                                <div className="mt-2 font-mono text-xs">
                                                    <span className={active ? "text-neon font-bold" : "text-ink"}>{formatPrice(v.price_usd)}</span>
                                                </div>
                                                {active && <div className="mt-1 overline text-[8px] text-neon">SELECTED</div>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Prominent sold-out banner */}
                        {soldOut && (
                            <div className="mt-5 bg-red-600/15 border-2 border-red-500 px-5 py-4" data-testid="sold-out-banner">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-red-600 text-white flex items-center justify-center font-display font-bold text-xl">⊘</div>
                                    <div>
                                        <p className="font-display font-bold text-red-400 text-lg uppercase tracking-wide">Sold Out</p>
                                        <p className="text-xs text-ink-dim mt-0.5">This variant is temporarily unavailable, drop your email below to get notified.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2 mt-6" data-testid="product-badges">
                            <a href={product.coa_url} target="_blank" rel="noreferrer"
                                className="group inline-flex items-center gap-2 px-3 py-1.5 bg-neon/10 text-neon text-[11px] font-bold font-mono uppercase tracking-wider hover:bg-neon/20 transition-colors border border-neon/30"
                                data-testid="coa-badge-link">
                                <ShieldCheck className="w-3.5 h-3.5" /> JANOSHIK COA <ExternalLink className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </a>
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-ink-surface text-ink-dim text-[11px] font-bold font-mono uppercase tracking-wider border border-ink-strong">
                                <Package className="w-3.5 h-3.5" /> DISCREET SHIPPING
                            </span>
                            {product.ships_from ? (
                                <span className={`inline-flex items-center gap-2 px-3 py-1.5 bg-ink-surface text-[11px] font-bold font-mono uppercase tracking-wider border ${product.ships_from.country === "CN" ? "text-orange-400 border-orange-400/40" : "text-gold border-gold/30"}`}
                                    data-testid="ships-from-badge"
                                    title={product.ships_from.note || ""}>
                                    {product.ships_from.country === "CN" ? "🇨🇳" : "🇨🇦"} {product.ships_from.label}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-ink-surface text-gold text-[11px] font-bold font-mono uppercase tracking-wider border border-gold/30">
                                    🇨🇦 CANADIAN WAREHOUSE
                                </span>
                            )}
                        </div>

                        {product.ships_from?.note && (
                            <p className="mt-4 text-xs text-orange-300 font-mono" data-testid="ships-from-note">⚠ {product.ships_from.note}</p>
                        )}

                        <p className="mt-8 text-base text-ink-dim leading-relaxed">{product.short}</p>

                        {/* Price + qty + live savings */}
                        <div className="mt-10 py-6 border-y border-ink-strong">
                            <div className="flex items-end justify-between flex-wrap gap-4">
                                <div>
                                    <p className="overline text-ink-mute mb-2">UNIT PRICE</p>
                                    {product.region === "EU" && product.price_eur ? (
                                        <div className="flex items-baseline gap-3" data-testid="dual-price-unit">
                                            <p className="font-display text-4xl font-bold text-ink" data-testid="product-price">€{Number(product.price_eur).toFixed(0)}</p>
                                            <p className="text-sm font-mono text-ink-mute">EU stock · ships from EU</p>
                                        </div>
                                    ) : product.sale_active && product.sale_price_usd ? (
                                        <div className="flex items-baseline gap-3" data-testid="dual-price-unit">
                                            <p className="font-display text-4xl font-bold text-gold" data-testid="product-price">
                                                {currency === "CAD" ? `CA$${(product.sale_price_usd * cadRate).toFixed(0)}` : `$${product.sale_price_usd.toFixed(0)} USD`}
                                            </p>
                                            <p className="text-xl font-mono text-ink-mute line-through">
                                                {currency === "CAD" ? `CA$${(product.price_usd * cadRate).toFixed(0)}` : `$${product.price_usd.toFixed(0)}`}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="flex items-baseline gap-3" data-testid="dual-price-unit">
                                            <p className="font-display text-4xl font-bold text-ink" data-testid="product-price">
                                                {currency === "CAD" ? `CA$${(product.price_usd * cadRate).toFixed(0)}` : `$${product.price_usd.toFixed(0)} USD`}
                                            </p>
                                            <p className="text-sm font-mono text-ink-mute">
                                                {currency === "CAD" ? `≈ $${product.price_usd.toFixed(0)} USD` : `≈ CA$${(product.price_usd * cadRate).toFixed(0)}`}
                                            </p>
                                        </div>
                                    )}
                                    {product.price_per_iu_usd && (
                                        <p className="mt-1.5 text-xs font-mono text-neon" data-testid="product-detail-iu">
                                            ≈ ${product.price_per_iu_usd.toFixed(2)} per IU · {(product.total_iu || 0).toLocaleString()} IU total
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center border border-ink-strong" data-testid="qty-selector">
                                    <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3 text-ink-dim hover:text-neon transition-colors" data-testid="qty-decrement"><Minus className="w-4 h-4" /></button>
                                    <span className="px-5 py-3 font-mono text-sm font-semibold text-ink min-w-[50px] text-center" data-testid="qty-value">{qty}</span>
                                    <button onClick={() => setQty(qty + 1)} className="px-3 py-3 text-ink-dim hover:text-neon transition-colors" data-testid="qty-increment"><Plus className="w-4 h-4" /></button>
                                </div>
                            </div>

                            {/* Bacteriostatic-water reconstitution notice, auto-shown on lyophilized peptides */}
                            {product.requires_bac_water && (
                                <div className="mt-4 p-3 border border-gold/40 bg-gold/5 flex items-start gap-2.5" data-testid="bac-water-notice">
                                    <span className="text-gold text-base shrink-0 mt-0.5">⚠</span>
                                    <div>
                                        <p className="text-xs font-bold text-gold mb-1 font-mono uppercase tracking-wider">Reconstitution required</p>
                                        <p className="text-xs text-ink-dim leading-relaxed">
                                            This compound requires <strong className="text-ink">bacteriostatic water</strong> for reconstitution. Don't forget to add a 10mL vial of BAC water to your order, available in <Link to="/product/bac-water-10ml" className="text-neon underline hover:text-neon-glow">Lab Supplies</Link>.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Made-to-order shipping + lead time notice */}
                            {product.made_to_order && (
                                <div className="mt-4 p-4 border-2 border-amber-500/60 bg-amber-500/10 flex items-start gap-3" data-testid="mto-notice">
                                    <span className="text-amber-400 text-xl shrink-0 mt-0.5">⚒</span>
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-amber-400 mb-1.5 font-mono uppercase tracking-wider">Made to order · adds {product.made_to_order_lead_time || "4 to 6 business days"}</p>
                                        <p className="text-xs text-ink-dim leading-relaxed">
                                            This kit is <strong className="text-ink">produced to order</strong> from fresh lab stock, not pulled from existing inventory. A flat <strong className="text-amber-400">${(product.made_to_order_fee_usd || 50).toFixed(0)} USD shipping</strong> is added once at checkout (regardless of how many MTO kits you order). Plan for {product.made_to_order_lead_time || "4 to 6 business days"} of additional production time on top of normal transit.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Live total + savings */}
                            <div className="mt-5 flex items-center justify-between text-sm">
                                <div className="text-ink-dim">
                                    <span className="font-mono">
                                        {`${qty} × ${formatPrice(product.price_usd)}`}
                                    </span>
                                    {activeTier && (
                                        <span className="ml-3 overline text-neon" data-testid="bundle-tier-badge">{activeTier.label} · {Math.round(activeTier.pct * 100)}% OFF</span>
                                    )}
                                </div>
                                <div className="text-right">
                                    {activeTier && (
                                        <div className="text-xs font-mono text-ink-mute line-through">{formatPrice(currentTotal)}</div>
                                    )}
                                    <div className="font-display text-xl font-bold text-ink" data-testid="live-total">{formatPrice(currentFinal)}</div>
                                    {activeTier && (
                                        <div className="overline text-[10px] text-neon" data-testid="live-savings">SAVE {formatPrice(currentDiscount)}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Near-Cart Pre-Order prominent banner (amber/orange, distinct from Best Value gold) */}
                        {isPreorder && !soldOut && (
                            <div className="mt-5 bg-amber-500/15 border border-amber-500/60 px-4 py-3 flex items-center gap-3" data-testid="preorder-near-cart">
                                <Clock className="w-5 h-5 text-amber-400 shrink-0" strokeWidth={2} />
                                <div>
                                    <p className="overline text-amber-400 text-[11px] font-bold">PRE-ORDER · Ships in 10 to 18 days</p>
                                    <p className="text-[11px] text-ink-dim mt-0.5">Fresh stock from lab. Reserved on purchase.</p>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={handleAdd}
                            disabled={soldOut}
                            className={`w-full mt-4 px-8 py-4 font-bold tracking-wide transition-colors inline-flex items-center justify-center gap-2 ${soldOut ? "bg-ink-elevated text-ink-mute cursor-not-allowed" : isPreorder ? "bg-amber-500 hover:bg-amber-400 text-black" : "bg-neon hover:bg-neon-glow text-black"}`}
                            data-testid="add-to-cart-button"
                        >
                            {soldOut ? (
                                <>⊘ SOLD OUT</>
                            ) : (
                                <>
                                    <Check className="w-4 h-4" /> {isPreorder ? "PRE-ORDER" : "ADD"} {qty} × {product.variant_label || product.dosage} {isPreorder ? "" : "TO CART"}
                                    <ArrowUpRight className="w-4 h-4" />
                                </>
                            )}
                        </button>

                        {/* Sold-out notify form */}
                        {soldOut && (
                            <SoldOutNotify productId={product.id} />
                        )}

                        {/* How long a vial lasts / Dose calculator */}
                        {product.dose_calc ? (
                            <DoseCalculator dc={product.dose_calc} fallbackText={product.lasts} />
                        ) : product.lasts ? (
                            <div className="mt-6 p-5 border border-neon/30 bg-neon/5 relative" data-testid="lasts-block">
                                <div className="flex items-start gap-3">
                                    <Clock className="w-5 h-5 text-neon shrink-0 mt-0.5" strokeWidth={1.5} />
                                    <div>
                                        <p className="overline text-neon mb-1">HOW LONG A VIAL LASTS</p>
                                        <p className="text-sm text-ink leading-relaxed">{product.lasts}</p>
                                        <p className="mt-2 text-xs font-mono text-ink-mute">{product.dosage_info}</p>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div className="mt-4 p-4 bg-ink-surface border border-ink text-xs text-ink-dim leading-relaxed" data-testid="research-disclaimer-inline">
                            <p className="overline text-gold mb-1.5">FOR RESEARCH USE ONLY</p>
                            Not for human consumption. Intended for laboratory and in-vitro research applications.
                        </div>
                    </div>
                </div>

                {/* HGH KITS SHOWCASE (shown only on HGH products, kits ARE the bundles) */}
                {product.variant_group === "hgh" && variants && variants.length > 1 && (
                    <section className="mt-24 pt-16 border-t border-ink" data-testid="hgh-kits-module">
                        <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
                            <div className="md:col-span-8">
                                <p className="section-num mb-3">/ HGH KITS · FROM STARTER TO BULK</p>
                                <h2 className="font-display text-4xl sm:text-5xl font-bold text-ink leading-[0.95]">Bigger kit.<br /><span className="text-gold">Bigger savings.</span></h2>
                                <p className="mt-4 text-ink-dim text-sm max-w-xl">Each kit ships with a different vial format optimized for its total IU. Per-IU price drops with every step up, the larger the kit, the lower your per-IU cost.</p>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-4 gap-px bg-ink-strong">
                            {(() => {
                                // Sort all HGH variants by total IU (extracted from variant_label or dose_calc)
                                const enriched = [...variants].map((v) => {
                                    // Match "200 IU Kit · 20 × 10 IU" → totalIu=200, vials=20, vialIu=10
                                    const totalMatch = (v.variant_label || "").match(/([\d,]+)\s*IU\s*(?:Mega\s+|Bulk\s+|Value\s+|Starter\s+|Popular\s+)?Kit/i);
                                    const detailMatch = (v.variant_label || "").match(/(\d+)\s*[×x]\s*(\d+)\s*IU/i);
                                    const totalIu = totalMatch ? parseInt(totalMatch[1].replace(/,/g, ""), 10) : (v.dose_calc?.vial_amount || v.price_usd);
                                    const vials = detailMatch ? parseInt(detailMatch[1], 10) : 1;
                                    const vialIu = detailMatch ? parseInt(detailMatch[2], 10) : 0;
                                    return { ...v, _totalIu: totalIu, _vials: vials, _vialIu: vialIu, _perIu: v.price_usd / Math.max(1, totalIu) };
                                }).sort((a, b) => (a.variant_order || 0) - (b.variant_order || 0));
                                // Use the smallest kit's per-IU rate as the savings baseline
                                const baselinePerIu = Math.max(...enriched.map((v) => v._perIu));
                                return enriched.map((v) => {
                                    const active = v.slug === product.slug;
                                    const savings = Math.max(0, (baselinePerIu * v._totalIu) - v.price_usd);
                                    const isStarter = v._perIu === baselinePerIu;
                                    return (
                                        <div key={v.id} className={`relative p-6 bg-ink-surface ${active ? "ring-2 ring-neon -m-px z-10" : ""}`} data-testid={`hgh-kit-${v.id}`}>
                                            {active && <div className="absolute top-3 right-3 overline text-[9px] text-black bg-neon px-2 py-0.5 font-bold">SELECTED</div>}
                                            {v.best_value && <div className="absolute -top-3 left-6 overline text-[9px] text-black bg-gold px-2.5 py-1 font-bold">★ BEST VALUE</div>}
                                            <div className="font-display text-2xl font-bold text-ink leading-tight">{v._totalIu.toLocaleString()} IU</div>
                                            <div className="overline text-[10px] text-ink-mute mt-1">
                                                {v._vialIu > 0 ? `${v._vials} × ${v._vialIu} IU VIALS` : (v.variant_label || "").toUpperCase()}
                                            </div>
                                            <div className="mt-4 pt-4 border-t border-ink space-y-1.5 font-mono text-xs">
                                                <div className="flex justify-between text-ink text-base items-baseline"><span className="text-ink-mute text-[10px]">KIT PRICE</span><span className="font-display font-bold">{formatPrice(v.price_usd)}</span></div>
                                                <div className="flex justify-between text-gold"><span className="text-[10px]">PER IU</span><span className="font-bold">{formatPrice(v._perIu)}</span></div>
                                                {isStarter ? (
                                                    <div className="flex justify-between text-ink-mute"><span className="text-[10px]">SAVINGS</span><span className="font-mono text-[10px]">STARTER TIER</span></div>
                                                ) : savings > 0 ? (
                                                    <div className="flex justify-between text-neon"><span className="text-[10px]">YOU SAVE</span><span className="font-bold">−{formatPrice(savings)}</span></div>
                                                ) : null}
                                            </div>
                                            {!active ? (
                                                <button onClick={() => navigate(`/product/${v.slug}`)}
                                                    className="w-full mt-5 border border-ink-strong hover:border-neon hover:text-neon text-ink px-3 py-2.5 text-[10px] font-mono uppercase tracking-[0.15em] transition-colors">
                                                    SELECT KIT
                                                </button>
                                            ) : (
                                                <div className="w-full mt-5 bg-neon/10 border border-neon text-neon text-center px-3 py-2.5 text-[10px] font-mono uppercase tracking-[0.15em]">
                                                    CURRENT KIT
                                                </div>
                                            )}
                                    </div>
                                );
                            });
                            })()}
                        </div>
                    </section>
                )}

                {/* BUNDLE & SAVE MODULE (shows when the product has flat bundle pricing) */}
                {bundleCalc && product.flat_bundles && product.flat_bundles.length > 0 && (
                    <section className="mt-24 pt-16 border-t border-ink" data-testid="bundle-module">
                        <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
                            <div className="md:col-span-8">
                                <p className="section-num mb-3">/ BUNDLE &amp; SAVE · AGGRESSIVE PRICING</p>
                                <h2 className="font-display text-4xl sm:text-5xl font-bold text-ink leading-[0.95]">Stack up.<br /><span className="text-gold">Save big.</span></h2>
                                <p className="mt-4 text-ink-dim text-sm max-w-xl">Flat bundle prices on {product.variant_label || product.dosage} {product.name}. Discounts apply automatically at the cart.</p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-px bg-ink-strong">
                            {bundleCalc.map((t) => {
                                const isActive = activeTier && activeTier.qty === t.qty;
                                const pctRound = Math.round(t.pct * 100);
                                return (
                                    <div key={t.qty} className={`relative p-8 bg-ink-surface ${isActive ? "ring-2 ring-neon -m-px z-10" : ""}`} data-testid={`bundle-tier-${t.qty}`}>
                                        {isActive && <div className="absolute top-3 right-3 overline text-[9px] text-black bg-neon px-2 py-0.5 font-bold">ACTIVE</div>}
                                        {t.qty === 5 && <div className="absolute -top-3 left-8 overline text-[9px] text-black bg-gold px-2.5 py-1 font-bold">★ BEST VALUE</div>}
                                        <div className="flex items-baseline justify-between">
                                            <div className="font-display text-5xl font-bold text-ink">{t.qty}<span className="text-2xl text-ink-mute">×</span></div>
                                            <div className="overline text-neon">SAVE {pctRound}%</div>
                                        </div>
                                        <div className="mt-4 font-display text-lg font-bold text-ink">{t.label}</div>
                                        <p className="mt-2 text-xs text-ink-mute">{t.desc}</p>

                                        <div className="mt-6 pt-6 border-t border-ink space-y-1.5 font-mono text-xs">
                                            <div className="flex justify-between text-ink-mute"><span>Regular price</span><span className="line-through">{formatPrice(t.full)}</span></div>
                                            <div className="flex justify-between text-ink text-base items-baseline"><span>Bundle price</span><span className="font-display font-bold">{formatPrice(t.discounted)}</span></div>
                                            <div className="flex justify-between text-gold"><span>Per vial</span><span className="font-bold">{formatPrice(t.perVial)}</span></div>
                                            {t.savings > 0 ? (
                                                <div className="flex justify-between text-neon"><span>You save</span><span className="font-bold">{formatPrice(t.savings)}</span></div>
                                            ) : (
                                                <div className="flex justify-between text-ink-mute"><span>Volume tier</span><span className="font-mono text-[10px]">FLAT BULK PRICING</span></div>
                                            )}
                                        </div>

                                        <button
                                            onClick={() => setQty(t.qty)}
                                            className="w-full mt-6 border border-ink-strong hover:border-neon hover:text-neon text-ink px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] transition-colors"
                                            data-testid={`bundle-set-${t.qty}`}
                                        >
                                            SET QTY TO {t.qty}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* ================================================================
                    RESEARCH DOSSIER, fully unfolded, no accordion.
                    Same beginner-friendly aesthetic as the crypto guide:
                    big headings, hero cards for the important stuff, plain English.
                    ================================================================ */}
                <section className="mt-24" data-testid="research-dossier">
                    <p className="section-num mb-4">/ RESEARCH DOSSIER</p>
                    <h2 className="font-display text-4xl sm:text-5xl font-bold text-ink tracking-tight leading-[0.92] mb-3">
                        Everything we know<br />about <span className="text-neon">{product.name.replace(/\s*\(.*?\)\s*$/, "")}.</span>
                    </h2>
                    <p className="text-base text-ink-dim leading-relaxed max-w-3xl">
                        Direct from the research literature. Every section is sourced from peer-reviewed peptide studies and clearly labelled when effects come from animal or cell-line models.
                    </p>

                    {/* ===== 01 · WHAT WE KNOW ===== */}
                    <div className="mt-12">
                        <p className="overline text-gold mb-3 text-[10px]">/ 01 · WHAT WE KNOW</p>
                        <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink leading-tight mb-4">
                            The background.
                        </h3>
                        <p className="text-base text-ink-dim leading-relaxed max-w-3xl">{product.what_we_know}</p>
                    </div>

                    {/* ===== 02 · WHAT IT DOES FOR YOU ===== */}
                    <div className="mt-12">
                        <p className="overline text-gold mb-3 text-[10px]">/ 02 · WHAT IT DOES FOR YOU</p>
                        <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink leading-tight mb-4">
                            What researchers study it for.
                        </h3>
                        {product.tag && (
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 border border-neon/40 bg-neon/5">
                                <span className={`w-1.5 h-1.5 rounded-full ${product.tag_color === "gold" ? "bg-gold" : "bg-neon"}`} />
                                <span className={`font-mono text-[10px] font-bold uppercase tracking-wider ${product.tag_color === "gold" ? "text-gold" : "text-neon"}`}>
                                    {product.tag}
                                </span>
                            </div>
                        )}
                        <p className="text-base text-ink-dim leading-relaxed max-w-3xl">{product.what_it_does}</p>
                        {product.info_breakdown?.research?.length > 0 && (
                            <div className="mt-6 p-5 border border-neon/30 bg-neon/5 max-w-3xl">
                                <p className="overline text-neon text-[10px] mb-3">RESEARCH HIGHLIGHTS · published animal &amp; cell-model studies</p>
                                <ul className="space-y-2.5">
                                    {product.info_breakdown.research.map((r, i) => (
                                        <li key={i} className="flex items-start gap-2.5 text-sm text-ink leading-relaxed">
                                            <span className="text-neon text-xs mt-1.5 shrink-0">▸</span>
                                            <span>{r}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-4 pt-3 border-t border-neon/20 text-[10px] font-mono uppercase tracking-wider text-ink-mute leading-snug">
                                    Sourced from peer-reviewed research literature. Effects described are from animal &amp; cell-line models unless otherwise noted. For research use only.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* ===== 03 · HOW IT WORKS (mechanism, if breakdown exists) ===== */}
                    {product.info_breakdown?.mechanism?.length > 0 && (
                        <div className="mt-12">
                            <p className="overline text-gold mb-3 text-[10px]">/ 03 · HOW IT WORKS</p>
                            <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink leading-tight mb-4">
                                Mechanism of action.
                            </h3>
                            <p className="text-sm text-ink-dim mb-5 max-w-3xl">
                                Biological pathways identified in the research literature for {product.name.replace(/\s*\(.*?\)\s*$/, "")}.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
                                {product.info_breakdown.mechanism.map((m, i) => (
                                    <div key={i} className="p-4 border border-ink-strong bg-ink-surface">
                                        <p className="text-sm text-ink leading-relaxed">{m}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ===== 04 · OVERVIEW & CLASSIFICATION (structural) ===== */}
                    {product.info_breakdown?.overview && (
                        <div className="mt-12">
                            <p className="overline text-gold mb-3 text-[10px]">/ 04 · OVERVIEW &amp; CLASSIFICATION</p>
                            <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink leading-tight mb-4">
                                Classification.
                            </h3>
                            {product.info_breakdown.overview.summary && (
                                <p className="text-base text-ink-dim leading-relaxed max-w-3xl mb-5">{product.info_breakdown.overview.summary}</p>
                            )}
                            <div className="grid sm:grid-cols-3 gap-3 max-w-3xl">
                                {product.info_breakdown.overview.type && (
                                    <div className="p-4 border border-ink-strong bg-ink-surface">
                                        <p className="overline text-[10px] text-ink-mute mb-1">TYPE</p>
                                        <p className="text-sm text-ink">{product.info_breakdown.overview.type}</p>
                                    </div>
                                )}
                                {product.info_breakdown.overview.derivation && (
                                    <div className="p-4 border border-ink-strong bg-ink-surface">
                                        <p className="overline text-[10px] text-ink-mute mb-1">DERIVATION</p>
                                        <p className="text-sm text-ink">{product.info_breakdown.overview.derivation}</p>
                                    </div>
                                )}
                                {product.info_breakdown.overview.structural && (
                                    <div className="p-4 border border-ink-strong bg-ink-surface">
                                        <p className="overline text-[10px] text-ink-mute mb-1">STRUCTURE</p>
                                        <p className="text-sm text-ink font-mono break-words leading-relaxed">{product.info_breakdown.overview.structural}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ===== 05 · PHARMACOLOGICAL PROFILE ===== */}
                    {product.info_breakdown?.pharmacology && (
                        <div className="mt-12">
                            <p className="overline text-gold mb-3 text-[10px]">/ 05 · PHARMACOLOGICAL PROFILE</p>
                            <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink leading-tight mb-4">
                                Pharmacology.
                            </h3>
                            <div className="grid sm:grid-cols-3 gap-3 max-w-3xl">
                                {[
                                    { l: "HALF-LIFE", v: product.info_breakdown.pharmacology.half_life },
                                    { l: "ABSORPTION", v: product.info_breakdown.pharmacology.absorption },
                                    { l: "DISTRIBUTION", v: product.info_breakdown.pharmacology.distribution },
                                ].filter((x) => x.v).map((x, i) => (
                                    <div key={i} className="p-4 border border-gold/40 bg-gold/5">
                                        <p className="overline text-[10px] text-gold mb-2">{x.l}</p>
                                        <p className="text-sm text-ink leading-relaxed">{x.v}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ===== 06 · DOSAGE & RECONSTITUTION ===== */}
                    <div className="mt-12">
                        <p className="overline text-gold mb-3 text-[10px]">/ 06 · DOSAGE &amp; RECONSTITUTION</p>
                        <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink leading-tight mb-4">
                            Research dosing protocols.
                        </h3>
                        {product.info_breakdown?.usage?.length > 0 ? (
                            <div className="space-y-2 max-w-3xl">
                                {product.info_breakdown.usage.map((u, i) => (
                                    <div key={i} className="flex items-start gap-3 p-3 border border-ink-strong bg-ink-surface">
                                        <span className="text-neon text-xs mt-0.5 shrink-0 font-mono">▸</span>
                                        <span className="text-sm text-ink leading-relaxed font-mono">{u}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-base text-ink-dim leading-relaxed font-mono max-w-3xl">{product.dosage_info}</p>
                        )}
                    </div>

                    {/* ===== 07 · PAIRS WELL WITH ===== */}
                    {product.pairs_with?.length > 0 && (
                        <div className="mt-12">
                            <p className="overline text-gold mb-3 text-[10px]">/ 07 · PAIRS WELL WITH</p>
                            <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink leading-tight mb-4">
                                Researched stacks.
                            </h3>
                            <p className="text-sm text-ink-dim mb-5 max-w-3xl">
                                Compounds explored alongside {product.name.replace(/\s*\(.*?\)\s*$/, "")} in the published literature.
                            </p>
                            <div className="space-y-3 max-w-3xl">
                                {product.pairs_with.map((pair, i) => (
                                    <div key={i} className="p-5 border-l-2 border-gold bg-ink-surface">
                                        <p className="font-display text-base font-bold text-ink mb-2">{pair.name}</p>
                                        <p className="text-sm text-ink-dim leading-relaxed">{pair.why}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ===== 08 · SAFETY PROFILE ===== */}
                    {product.info_breakdown?.safety?.length > 0 && (
                        <div className="mt-12">
                            <p className="overline text-gold mb-3 text-[10px]">/ 08 · SAFETY PROFILE</p>
                            <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink leading-tight mb-4">
                                Safety notes.
                            </h3>
                            <div className="p-5 border border-red-500/30 bg-red-500/5 max-w-3xl" data-testid="safety-block">
                                <ul className="space-y-2.5">
                                    {product.info_breakdown.safety.map((s, i) => (
                                        <li key={i} className="flex items-start gap-2.5 text-sm text-ink leading-relaxed">
                                            <span className="text-red-400 text-xs mt-1.5 shrink-0">▸</span>
                                            <span>{s}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-4 pt-3 border-t border-red-500/20 text-[10px] font-mono uppercase tracking-wider text-red-300/80 leading-snug">
                                    Research use only · not for human consumption · always consult published literature before designing any research protocol.
                                </p>
                            </div>
                        </div>
                    )}
                </section>
            </div>
            <RecentlyViewedRail excludeId={product.id} subtitle="Also recently viewed" />
        </div>
    );
}


// SEO: injects per-product schema.org JSON-LD + canonical/title/description.
// Lightweight inline implementation (no react-helmet dependency).
function ProductSEO({ product }) {
    React.useEffect(() => {
        if (!product) return;
        const isEU = false;
        const price = (product.sale_price_usd || product.price_usd);
        const currency = "USD";
        const title = `${product.name} ${product.variant_label || product.dosage} · Helixora Peptides`;
        const desc = (product.short || product.what_we_know || "Lab-verified research peptide. Janoshik-tested. Discreet shipping.").slice(0, 160);
        const url = `https://helixorapeptides.com/product/${product.slug}`;
        const setMeta = (sel, attr, value) => {
            let el = document.querySelector(sel);
            if (!el) { el = document.createElement("meta"); document.head.appendChild(el); }
            const [k, v] = attr.split("=");
            if (k && v) el.setAttribute(k, v.replace(/['"]/g, ""));
            el.setAttribute("content", value);
        };
        document.title = title;
        setMeta(`meta[name="description"]`, `name="description"`, desc);
        setMeta(`meta[property="og:title"]`, `property="og:title"`, title);
        setMeta(`meta[property="og:description"]`, `property="og:description"`, desc);
        setMeta(`meta[property="og:url"]`, `property="og:url"`, url);
        if (product.image) setMeta(`meta[property="og:image"]`, `property="og:image"`, product.image);
        setMeta(`meta[name="twitter:title"]`, `name="twitter:title"`, title);
        setMeta(`meta[name="twitter:description"]`, `name="twitter:description"`, desc);
        if (product.image) setMeta(`meta[name="twitter:image"]`, `name="twitter:image"`, product.image);
        // Canonical link
        let link = document.querySelector(`link[rel="canonical"]`);
        if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
        link.setAttribute("href", url);
        // schema.org Product JSON-LD
        const schema = {
            "@context": "https://schema.org/",
            "@type": "Product",
            name: product.name + " " + (product.variant_label || product.dosage),
            description: desc,
            image: product.image || (product.images && product.images[0]) || undefined,
            sku: product.id,
            brand: { "@type": "Brand", name: "Helixora Peptides" },
            offers: {
                "@type": "Offer",
                priceCurrency: currency,
                price: price,
                availability: product.in_stock === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
                url,
            },
        };
        let s = document.getElementById("product-jsonld");
        if (!s) { s = document.createElement("script"); s.id = "product-jsonld"; s.type = "application/ld+json"; document.head.appendChild(s); }
        s.textContent = JSON.stringify(schema);
        return () => { /* leave tags in place between SPA navigations; updated on next mount */ };
    }, [product]);
    return null;
}
